/**
 * Domain Detection Utility
 *
 * Detects whether the current request is from:
 * 1. Main app (trainerpulse.app, my-gym-dash.pages.dev, localhost)
 * 2. Subdomain (fitpro.trainerpulse.app)
 * 3. Custom domain (portal.fitpro.com)
 *
 * This runs BEFORE auth to determine which organization's branding to show.
 */

export interface DomainInfo {
  /** Type of domain access */
  type: 'main' | 'subdomain' | 'custom';
  /** The subdomain identifier or full custom domain */
  identifier: string | null;
  /** Full hostname */
  hostname: string;
  /** Whether this is the main TrainerPulse app */
  isMainApp: boolean;
  /** The full origin URL */
  origin: string;
}

// Main app domains that should NOT trigger white-label mode
const MAIN_APP_DOMAINS = [
  'trainerpulse.app',
  'www.trainerpulse.app',
  'my-gym-dash.pages.dev',
  'localhost',
  '127.0.0.1',
];

// The base domain for subdomains
const APP_BASE_DOMAIN = 'trainerpulse.app';

// Reserved subdomains that are NOT organization subdomains
const RESERVED_SUBDOMAINS = [
  'www',
  'app',
  'api',
  'admin',
  'help',
  'support',
  'blog',
  'mail',
  'email',
  'cdn',
  'static',
  'assets',
];

/**
 * Detect the domain type and identifier from the current URL
 */
export function detectDomain(hostname?: string): DomainInfo {
  const host = hostname || (typeof window !== 'undefined' ? window.location.hostname : 'localhost');
  const origin = typeof window !== 'undefined' ? window.location.origin : `https://${host}`;

  // Check if it's one of the main app domains
  if (MAIN_APP_DOMAINS.includes(host)) {
    return {
      type: 'main',
      identifier: null,
      hostname: host,
      isMainApp: true,
      origin,
    };
  }

  // Check for localhost with port (development)
  if (host.includes('localhost') || host === '127.0.0.1') {
    return {
      type: 'main',
      identifier: null,
      hostname: host,
      isMainApp: true,
      origin,
    };
  }

  // Check if it's a subdomain of trainerpulse.app
  if (host.endsWith(`.${APP_BASE_DOMAIN}`)) {
    const subdomain = host.replace(`.${APP_BASE_DOMAIN}`, '');

    // Skip reserved subdomains
    if (RESERVED_SUBDOMAINS.includes(subdomain.toLowerCase())) {
      return {
        type: 'main',
        identifier: null,
        hostname: host,
        isMainApp: true,
        origin,
      };
    }

    return {
      type: 'subdomain',
      identifier: subdomain.toLowerCase(),
      hostname: host,
      isMainApp: false,
      origin,
    };
  }

  // Check if it's a subdomain of the Cloudflare Pages preview domain
  if (host.endsWith('.my-gym-dash.pages.dev')) {
    const subdomain = host.replace('.my-gym-dash.pages.dev', '');

    // Skip Cloudflare preview branch deploys (they have hashes)
    if (subdomain.length > 20 || subdomain.includes('-')) {
      return {
        type: 'main',
        identifier: null,
        hostname: host,
        isMainApp: true,
        origin,
      };
    }

    return {
      type: 'subdomain',
      identifier: subdomain.toLowerCase(),
      hostname: host,
      isMainApp: false,
      origin,
    };
  }

  // It's a custom domain
  return {
    type: 'custom',
    identifier: host.toLowerCase(),
    hostname: host,
    isMainApp: false,
    origin,
  };
}

/**
 * Get the domain identifier for database lookups
 * Returns null for main app, subdomain string for subdomains, full hostname for custom domains
 */
export function getDomainIdentifier(): string | null {
  const domainInfo = detectDomain();
  return domainInfo.identifier;
}

/**
 * Check if we're on a white-label domain (subdomain or custom domain)
 */
export function isWhiteLabelDomain(): boolean {
  const domainInfo = detectDomain();
  return !domainInfo.isMainApp;
}

/**
 * Get the main app URL (for redirects, OAuth callbacks, etc.)
 */
export function getMainAppUrl(): string {
  // In development, use localhost
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return window.location.origin;
  }

  // In production, always use the main app domain
  return 'https://trainerpulse.app';
}

/**
 * Get the current domain's origin URL
 */
export function getCurrentOrigin(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return getMainAppUrl();
}

/**
 * Build a URL for the current domain
 */
export function buildDomainUrl(path: string): string {
  const origin = getCurrentOrigin();
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Build a URL for a specific subdomain
 */
export function buildSubdomainUrl(subdomain: string, path: string = '/'): string {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    // In development, we can't actually use subdomains without /etc/hosts
    // So we'll use query params as a fallback for testing
    return `${window.location.origin}${path}?org=${subdomain}`;
  }

  return `https://${subdomain}.${APP_BASE_DOMAIN}${path}`;
}
